import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService } from './api.service';
import { I18nService, TranslatePipe } from './i18n.service';
import { aucklandClockParts, aucklandDateKey } from './auckland-time';

type ListKind =
  | 'jobs-assigned'
  | 'jobs-active'
  | 'jobs-paused'
  | 'jobs-today'
  | 'jobs-upcoming'
  | 'jobs-quotes'
  | 'jobs-office'
  | 'jobs-completed'
  | 'projects-assigned'
  | 'projects-completed';

@Component({
  selector: 'app-work-list',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslatePipe],
  template: `
    <div class="min-h-screen lg:min-h-[calc(100vh-72px)] flex flex-col pb-24 lg:pb-8" style="background:#F7FBFD;">
      <header class="px-5 pt-4 pb-4 sticky top-0 z-30 text-white shadow-sm flex items-center gap-3 lg:hidden"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <button (click)="goBack()" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15 shrink-0">
          <i class="ti ti-arrow-left text-[20px]"></i>
        </button>
        <div class="min-w-0 flex-1">
          <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">{{ isProjectList() ? ('projects' | t) : ('jobs' | t) }}</p>
          <h1 class="text-lg font-semibold text-white/95 truncate">{{ config().title }}</h1>
        </div>
        <span class="text-[10px] font-medium px-2.5 py-1 rounded-full uppercase border bg-white/10 text-white/75 border-white/15">
          {{ filteredItems().length }}
        </span>
      </header>

      <main class="flex-1 px-4 pt-5 space-y-4 lg:px-7 lg:pt-6">
        <section class="bg-white rounded-2xl lg:rounded-lg p-4 border border-slate-100">
          <p class="text-sm font-semibold text-slate-800">{{ config().title }}</p>
          <p class="text-xs text-slate-500 mt-0.5">{{ config().description }}</p>
        </section>

        @if (loadError()) {
          <section class="bg-rose-50 border border-rose-100 rounded-2xl p-4 flex items-start gap-3">
            <i class="ti ti-alert-circle text-rose-500 text-xl shrink-0"></i>
            <div class="flex-1">
              <p class="text-sm font-semibold text-rose-800">Unable to load list</p>
              <p class="text-xs text-rose-600 mt-0.5">{{ loadError() }}</p>
            </div>
            <button (click)="loadData()" class="text-xs font-semibold text-rose-700 bg-white/70 border border-rose-100 rounded-lg px-3 py-1.5">{{ 'retry' | t }}</button>
          </section>
        }

        @if (isLoading()) {
          <section class="space-y-3 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-3 lg:space-y-0">
            @for (n of [1,2,3,4,5]; track n) {
              <div class="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3 animate-pulse">
                <div class="w-10 h-10 rounded-xl bg-slate-100"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-3 bg-slate-100 rounded-full w-1/3"></div>
                  <div class="h-4 bg-slate-100 rounded-full w-2/3"></div>
                  <div class="h-3 bg-slate-100 rounded-full w-1/2"></div>
                </div>
              </div>
            }
          </section>
        } @else if (filteredItems().length === 0) {
          <section class="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-slate-100">
            <div class="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-4">
              <i class="ti ti-clipboard-off text-2xl text-slate-300"></i>
            </div>
            <h3 class="font-semibold text-slate-700 text-base mb-1">Nothing Here</h3>
            <p class="text-slate-400 text-sm max-w-xs leading-relaxed">{{ config().emptyText }}</p>
          </section>
        } @else {
          <section class="space-y-3">
            @if (isProjectList()) {
              @for (project of filteredItems(); track project.id) {
                <a [routerLink]="['/project', project.id]" class="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3 active:scale-[0.98] transition-all">
                  <div class="w-10 h-10 rounded-xl bg-sky-50 text-[#1A6FA6] flex items-center justify-center shrink-0">
                    <i class="ti ti-building text-xl"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="font-semibold text-sm text-slate-800 truncate">{{ project.name }}</h3>
                    <p class="text-xs text-slate-500 truncate mt-0.5"><i class="ti ti-map-pin"></i> {{ project.address || '-' }}</p>
                  </div>
                  <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase border"
                        [class.bg-emerald-50]="project.status === 'Completed'"
                        [class.text-emerald-700]="project.status === 'Completed'"
                        [class.border-emerald-100]="project.status === 'Completed'"
                        [class.bg-amber-50]="project.status !== 'Completed'"
                        [class.text-amber-700]="project.status !== 'Completed'"
                        [class.border-amber-100]="project.status !== 'Completed'">{{ project.status }}</span>
                </a>
              }
            } @else {
              @for (job of filteredItems(); track job.job_id) {
                <a [routerLink]="['/job', job.job_id]" class="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3 active:scale-[0.98] transition-all">
                  <div class="w-12 h-12 rounded-xl shrink-0 border flex flex-col items-center justify-center"
                       [class.bg-sky-50]="job.status !== 'In Progress' && job.status !== 'Paused'"
                       [class.text-[#1A6FA6]]="job.status !== 'In Progress' && job.status !== 'Paused'"
                       [class.border-sky-100]="job.status !== 'In Progress' && job.status !== 'Paused'"
                       [class.bg-emerald-50]="job.status === 'In Progress'"
                       [class.text-emerald-700]="job.status === 'In Progress'"
                       [class.border-emerald-100]="job.status === 'In Progress'"
                       [class.bg-orange-50]="job.status === 'Paused'"
                       [class.text-orange-700]="job.status === 'Paused'"
                       [class.border-orange-100]="job.status === 'Paused'">
                    @if (job.status === 'In Progress') {
                      <i class="ti ti-player-play-filled text-lg"></i>
                      <span class="text-[9px] font-semibold uppercase">Live</span>
                    } @else if (job.status === 'Paused') {
                      <i class="ti ti-player-pause-filled text-lg"></i>
                      <span class="text-[9px] font-semibold uppercase">Pause</span>
                    } @else {
                      <span class="text-[9px] font-semibold uppercase">{{ job.timeParts[1] }}</span>
                      <span class="text-sm font-semibold">{{ job.timeParts[0] }}</span>
                    }
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span class="text-[10px] font-semibold text-[#1A6FA6]">{{ job.job_id }}</span>
                      <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase border"
                            [class.bg-orange-50]="job.status === 'Paused'"
                            [class.text-orange-700]="job.status === 'Paused'"
                            [class.border-orange-100]="job.status === 'Paused'"
                            [class.bg-slate-50]="job.status !== 'Paused'"
                            [class.text-slate-500]="job.status !== 'Paused'"
                            [class.border-slate-100]="job.status !== 'Paused'">{{ job.status }}</span>
                      @if (job.job_type && job.job_type !== 'Service') {
                        <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase border"
                              [class.bg-sky-50]="job.job_type === 'Inspection'"
                              [class.text-sky-700]="job.job_type === 'Inspection'"
                              [class.border-sky-100]="job.job_type === 'Inspection'"
                              [class.bg-rose-50]="job.job_type === 'Emergency'"
                              [class.text-rose-700]="job.job_type === 'Emergency'"
                              [class.border-rose-100]="job.job_type === 'Emergency'">{{ job.job_type }}</span>
                      }
                    </div>
                    <h3 class="font-semibold text-sm text-slate-800 truncate">{{ job.customer }}</h3>
                    <p class="text-xs text-slate-500 truncate mt-0.5"><i class="ti ti-map-pin"></i> {{ job.address }}</p>
                  </div>
                  <i class="ti ti-chevron-right text-slate-300 shrink-0"></i>
                </a>
              }
            }
          </section>
        }
      </main>
    </div>
  `
})
export class WorkListComponent implements OnInit {
  private api = inject(ApiService);
  private i18n = inject(I18nService);
  private route = inject(ActivatedRoute);
  private location = inject(Location);

  kind = signal<ListKind>('jobs-assigned');
  isLoading = signal(true);
  loadError = signal('');
  jobs = signal<any[]>([]);
  projects = signal<any[]>([]);

  config = computed(() => {
    const map: Record<ListKind, { title: string; description: string; emptyText: string }> = {
      'jobs-assigned': {
        title: this.i18n.translate('assignedJobs'),
        description: 'All jobs currently assigned to your account.',
        emptyText: 'No jobs are currently assigned to you.',
      },
      'jobs-active': {
        title: this.i18n.translate('live'),
        description: 'Jobs currently underway in the field.',
        emptyText: 'You have no jobs in progress.',
      },
      'jobs-paused': {
        title: this.i18n.translate('paused'),
        description: 'Jobs paused in the field and waiting to be resumed.',
        emptyText: 'You have no paused jobs.',
      },
      'jobs-today': {
        title: this.i18n.translate('todayWork'),
        description: 'Appointments scheduled for today.',
        emptyText: 'There are no jobs scheduled for today.',
      },
      'jobs-upcoming': {
        title: this.i18n.translate('upcomingWork'),
        description: 'Scheduled, approved, or unscheduled jobs waiting for action.',
        emptyText: 'There are no upcoming jobs in your queue.',
      },
      'jobs-quotes': {
        title: this.i18n.translate('quoteFollowUp'),
        description: 'Quote sent and approved jobs that need attention.',
        emptyText: 'There are no quote follow-up jobs right now.',
      },
      'jobs-office': {
        title: this.i18n.translate('readyForOffice'),
        description: 'Field work finished and ready for office processing.',
        emptyText: 'No jobs are waiting for office processing.',
      },
      'jobs-completed': {
        title: this.i18n.translate('completed'),
        description: 'Completed or terminated jobs.',
        emptyText: 'No completed jobs are available.',
      },
      'projects-assigned': {
        title: this.i18n.translate('projects'),
        description: 'Projects currently assigned to you.',
        emptyText: 'No projects are currently assigned to you.',
      },
      'projects-completed': {
        title: this.i18n.translate('completed'),
        description: 'Projects that have been completed.',
        emptyText: 'No completed projects are available.',
      },
    };
    return map[this.kind()] || map['jobs-assigned'];
  });

  isProjectList = computed(() => this.kind().startsWith('projects-'));
  filteredItems = computed(() => this.isProjectList() ? this.filteredProjects() : this.filteredJobs());

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      this.kind.set((params.get('kind') as ListKind) || 'jobs-assigned');
      this.loadData();
    });
  }

  loadData() {
    this.isLoading.set(true);
    this.loadError.set('');
    if (this.isProjectList()) {
      this.loadProjects();
    } else {
      this.loadJobs();
    }
  }

  goBack() {
    this.location.back();
  }

  private loadJobs() {
    this.api.getJobs().subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) {
          this.jobs.set((res.data?.jobs || []).map((j: any) => this.mapJob(j)));
        } else {
          this.loadError.set(res.message || 'Jobs could not be loaded.');
        }
      },
      error: (err: any) => {
        this.isLoading.set(false);
        this.loadError.set(err?.error?.message || 'Jobs could not be loaded.');
      }
    });
  }

  private loadProjects() {
    this.api.getProjects().subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) {
          this.projects.set(res.data?.projects || []);
        } else {
          this.loadError.set(res.message || 'Projects could not be loaded.');
        }
      },
      error: (err: any) => {
        this.isLoading.set(false);
        this.loadError.set(err?.error?.message || 'Projects could not be loaded.');
      }
    });
  }

  private filteredJobs() {
    const today = aucklandDateKey();
    const jobs = this.jobs();
    switch (this.kind()) {
      case 'jobs-active':
        return jobs.filter(j => j.status === 'In Progress');
      case 'jobs-paused':
        return jobs.filter(j => j.status === 'Paused');
      case 'jobs-today':
        return jobs.filter(j => j.scheduledDay === today && ['Scheduled', 'Unassigned', 'Approved', 'Quote Sent'].includes(j.status));
      case 'jobs-upcoming':
        return jobs.filter(j => ['Unassigned', 'Scheduled', 'Approved'].includes(j.status));
      case 'jobs-quotes':
        return jobs.filter(j => j.status === 'Quote Sent');
      case 'jobs-office':
        return jobs.filter(j => j.status === 'Ready for Office');
      case 'jobs-completed':
        return jobs.filter(j => ['Completed', 'Terminated'].includes(j.status));
      default:
        return jobs;
    }
  }

  private filteredProjects() {
    if (this.kind() === 'projects-completed') {
      return this.projects().filter(p => p.status === 'Completed');
    }
    return this.projects().filter(p => p.status !== 'Completed');
  }

  private mapJob(j: any) {
    const scheduledDate = j.scheduled_date ? new Date(j.scheduled_date) : null;
    let timeParts = ['--', ''];
    let scheduledDay = '';
    if (scheduledDate && !Number.isNaN(scheduledDate.getTime())) {
      scheduledDay = aucklandDateKey(scheduledDate);
      timeParts = aucklandClockParts(scheduledDate);
    }

    return {
      job_id: j.id,
      customer: j.contacts?.find((c: any) => c.contact_type === 'Customer' || c.type === 'Customer')?.name || j.customer_name || 'Unknown',
      address: j.address || 'Unknown Address',
      status: j.status,
      job_type: j.type || j.job_type || 'Service',
      scheduledDay,
      pausedAt: j.pause_started_at || j.paused_at || null,
      timeParts,
    };
  }

}
